import React, { useState, useEffect } from "react";
import "./SignUp.css";
import { Link, useNavigate } from "react-router-dom";
import { auth, googleProvider } from "../../firebase/firebase";
import { signInWithPopup, createUserWithEmailAndPassword } from "firebase/auth";

const SignUp = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    fname: "",
    email: "",
    password: "",
    confirmPassword: "",
    cNumber: "",
    countryCode: "+91",
    userType: "user",
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false); // for social login

  const handleChange = (e) =>
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  // ---------------- EMAIL SIGNUP ----------------
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!formData.email || !formData.password || !formData.fname)
      return setError("Please fill all required fields.");
    if (formData.password !== formData.confirmPassword)
      return setError("Passwords do not match.");

    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        formData.email,
        formData.password
      );
      const user = userCredential.user;
      const idToken = await user.getIdToken();

      const res = await fetch("https://agepcoatings.com/registersocial", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          fname: formData.fname,
          email: formData.email,
          cNumber: `${formData.countryCode}${formData.cNumber}`,
          userType: formData.userType,
        }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Registration failed");

      // ✅ Save user in localStorage
      localStorage.setItem(
        "user",
        JSON.stringify({
          uid: user.uid,
          email: user.email,
          displayName: formData.fname,
          photoURL: user.photoURL || "",
        })
      );

      window.dispatchEvent(new Event("userChanged"));

      setSuccess("Registration successful!");
      setFormData({
        fname: "",
        email: "",
        password: "",
        confirmPassword: "",
        cNumber: "",
        countryCode: "+91",
        userType: "user",
      });

      navigate("/registerprofile");
    } catch (err) {
      console.error(err);
      setError(err.message);
    }
  };

  // ---------------- GOOGLE SIGNUP ----------------
  const handleSocialRegister = async (provider) => {
    if (loading) return; // prevent multiple clicks
    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      const idToken = await user.getIdToken();

      const res = await fetch("https://agepcoatings.com/registersocial", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          fname: user.displayName || "",
          email: user.email || "",
          cNumber: formData.cNumber
            ? `${formData.countryCode}${formData.cNumber}`
            : null,
          userType: "user",
        }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Social registration failed");

      // ✅ Save Google user in localStorage
      localStorage.setItem(
        "user",
        JSON.stringify({
          uid: user.uid,
          email: user.email,
          displayName: user.displayName || "",
          photoURL: user.photoURL || "",
        })
      );

      window.dispatchEvent(new Event("userChanged"));

      setSuccess("SignUp successful!");
      navigate("/registerprofile");
    } catch (err) {
      console.error("Social register error", err);

      if (err.code === "auth/popup-closed-by-user") {
        setError("Google sign-in was closed before completion.");
      } else if (err.code === "auth/cancelled-popup-request") {
        setError("Sign-in request was cancelled. Please try again.");
      } else {
        setError(err.message || "SIgn Up error");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(""), 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  return (
    <div className="container signup-container">
      <div className="row">
        <div className="col-lg-12 py-5 signup-right">
          <h3 className="fw-bold mb-4 text-center">Sign Up</h3>

          {error && <div className="alert alert-danger">{error}</div>}
          {success && <div className="alert alert-success">{success}</div>}

          <form onSubmit={handleSubmit}>
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">Full Name *</label>
                <input
                  type="text"
                  className="form-control"
                  name="fname"
                  value={formData.fname}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">Email *</label>
                <input
                  type="email"
                  className="form-control"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            <div className="mb-3">
              <label className="form-label">Contact Number</label>
              <div className="input-group">
                <select
                  className="form-select"
                  name="countryCode"
                  value={formData.countryCode}
                  onChange={handleChange}
                  style={{ maxWidth: "120px" }}
                >
                  <option value="+91">+91</option>
                  <option value="+1">+1</option>
                  <option value="+44">+44</option>
                  <option value="+61">+61</option>
                  <option value="+81">+81</option>
                </select>
                <input
                  type="text"
                  className="form-control"
                  name="cNumber"
                  value={formData.cNumber}
                  onChange={handleChange}
                  placeholder="Enter phone number"
                />
              </div>
            </div>

            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">Password *</label>
                <div className="input-group">
                  <input
                    type={showPassword ? "text" : "password"}
                    className="form-control"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    required
                  />
                  <span
                    className="input-group-text"
                    onClick={() => setShowPassword(!showPassword)}
                    style={{ cursor: "pointer" }}
                  >
                    <i
                      className={`bi ${
                        showPassword ? "bi-eye-slash" : "bi-eye"
                      }`}
                    ></i>
                  </span>
                </div>
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">Confirm Password *</label>
                <div className="input-group">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    className="form-control"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    required
                  />
                  <span
                    className="input-group-text"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    style={{ cursor: "pointer" }}
                  >
                    <i
                      className={`bi ${
                        showConfirmPassword ? "bi-eye-slash" : "bi-eye"
                      }`}
                    ></i>
                  </span>
                </div>
              </div>
            </div>

            <button
              type="submit"
              className="custom-register-btn border-0 w-100 mb-3"
            >
              Sign Up
            </button>

            <div className="text-center mb-3">OR</div>

            <button
              type="button"
              className="google-btn border-0 w-100 mb-3 d-flex align-items-center justify-content-center gap-2"
              onClick={() => handleSocialRegister(googleProvider)}
              disabled={loading}
            >
              {loading ? (
                "Signing in..."
              ) : (
                <>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="18"
                    viewBox="0 0 48 48"
                  >
                    <path
                      fill="#4285F4"
                      d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.84-6.84C35.4 2.92 29.97 0 24 0 14.6 0 6.44 5.45 2.56 13.36l7.91 6.14C12.44 13.13 17.73 9.5 24 9.5z"
                    />
                    <path
                      fill="#34A853"
                      d="M46.5 24.5c0-1.54-.14-3.02-.41-4.44H24v8.41h12.65c-.55 2.96-2.17 5.47-4.6 7.17l7.1 5.49C43.93 37.61 46.5 31.5 46.5 24.5z"
                    />
                    <path
                      fill="#FBBC05"
                      d="M10.47 28.05c-.48-1.39-.75-2.87-.75-4.55s.27-3.16.75-4.55l-7.91-6.14C1.06 15.45 0 19.57 0 24c0 4.43 1.06 8.55 2.56 12.14l7.91-6.14z"
                    />
                    <path
                      fill="#EA4335"
                      d="M24 48c6.48 0 11.9-2.13 15.87-5.81l-7.1-5.49c-2.02 1.35-4.63 2.14-8.77 2.14-6.27 0-11.56-3.63-13.53-8.86l-7.91 6.14C6.44 42.55 14.6 48 24 48z"
                    />
                  </svg>
                  Continue with Google
                </>
              )}
            </button>

            <p className="text-center text-secondary mb-0">
              Already have an account?{" "}
              <Link
                className="text-muted link-underline link-underline-opacity-0"
                to="/login"
              >
                Login
              </Link>
            </p>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SignUp;
